import type {
  CreateProjectPayload,
  Paginated,
  PaginatedQuery,
  Project,
  ProjectListItem,
  UpdateProjectPayload,
} from '@atlas/api-contract';

import { apiClient } from './client';

/**
 * `/v1/projects`.
 *
 * Thin on purpose: no caching, no error handling, no shaping. The axios
 * interceptor has already turned every failure into an `ApiError` with a
 * readable message, and React Query owns everything else — so these functions
 * stay callable from a route loader, a mutation or a test without dragging a
 * cache along with them.
 */

/** Request options every read accepts, so React Query can cancel it. */
interface ReadOptions {
  signal?: AbortSignal | undefined;
}

/**
 * `signal` is spread conditionally rather than passed as `undefined`: axios
 * declares it without `| undefined`, and `exactOptionalPropertyTypes` is on.
 */
const withSignal = ({ signal }: ReadOptions) =>
  signal === undefined ? {} : { signal };

/** One page of projects, filtered and sorted server-side (spec R6.5, R6.8). */
export const listProjects = async (
  query: PaginatedQuery,
  options: ReadOptions = {},
): Promise<Paginated<ProjectListItem>> => {
  const { data } = await apiClient.get<Paginated<ProjectListItem>>('/projects', {
    params: query,
    ...withSignal(options),
  });

  return data;
};

/** One project. 404 when the id is unknown. */
export const getProject = async (
  projectId: string,
  options: ReadOptions = {},
): Promise<Project> => {
  const { data } = await apiClient.get<Project>(
    `/projects/${encodeURIComponent(projectId)}`,
    withSignal(options),
  );

  return data;
};

/** Creates a project. A duplicate name comes back as a 409. */
export const createProject = async (
  payload: CreateProjectPayload,
): Promise<Project> => {
  const { data } = await apiClient.post<Project>('/projects', payload);

  return data;
};

/** Partial update. Only the keys present are changed. */
export const updateProject = async (
  projectId: string,
  payload: UpdateProjectPayload,
): Promise<Project> => {
  const { data } = await apiClient.patch<Project>(
    `/projects/${encodeURIComponent(projectId)}`,
    payload,
  );

  return data;
};

/**
 * Deletes a project and, by cascade, every prototype, version, thread and
 * comment inside it. Returns 204 with no body.
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  await apiClient.delete(`/projects/${encodeURIComponent(projectId)}`);
};
